import { useState } from 'react';
import { MailWarning } from 'lucide-react';
import accountService from '../services/accountService';
import { toast } from '../ui';

/**
 * "Verify your email" prompt for a signed-in user whose email isn't confirmed yet. Purchases, GitHub access
 * updates and invoices are emailed, so a typo here means the customer never gets them.
 * Renders nothing when the user has no email or it's already verified.
 */
const EmailVerificationBanner = ({ user, onChangeEmail, required = false, className = '' }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user?.email || user.emailConfirmed) return null;

  const resend = async () => {
    setSending(true);
    const result = await accountService.sendVerificationEmail();
    setSending(false);
    if (result.success) {
      setSent(true);
      toast.success(result.message || 'Verification link sent');
    } else {
      toast.error({ content: result.error, duration: 6 });
    }
  };

  return (
    <div className={`pk-alert ${className}`.trim()} data-tone={required ? 'danger' : 'warning'} role="status">
      <MailWarning aria-hidden="true" />
      <div className="pk-stack" style={{ gap: 8 }}>
        <span>
          <strong>{required ? 'Verify your email to continue.' : 'Please verify your email address.'}</strong> We send your
          purchases and invoices to <strong>{user.email}</strong>.{' '}
          {sent ? 'Check your inbox (and spam folder) for the link.' : 'Open the link we emailed you.'} Wrong address?
          Change it.
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--xs"
            onClick={resend}
            disabled={sending}
            aria-busy={sending || undefined}
          >
            {sent ? 'Send another link' : 'Resend link'}
          </button>
          {onChangeEmail && (
            <button type="button" className="pk-btn pk-btn--ghost pk-btn--xs" onClick={onChangeEmail}>
              Change email
            </button>
          )}
        </span>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
